import type { components } from '@connected/api-client/api.generated';

import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export type Device = components['schemas']['ListElectricalProjectDistributionBoardDeviceQueryResultDevice'];
export type DeviceKind = components['schemas']['ElectricalProjectDistributionBoardDeviceKind'];

export function useDevices({
    electricalProjectVersionId,
    distributionBoardId,
    deviceKind,
}: {
    electricalProjectVersionId: string;
    distributionBoardId: string;
    deviceKind: DeviceKind | DeviceKind[];
}) {
    const deviceKinds = useMemo(() => Array.isArray(deviceKind) ? deviceKind : [deviceKind], [deviceKind]);

    if (!electricalProjectVersionId) {
        throw new Error('electricalProjectVersionId is required');
    }

    if (!distributionBoardId) {
        throw new Error('distributionBoardId is required');
    }

    if (!deviceKinds.length) {
        throw new Error('deviceKind is required');
    }

    const devices = useSuspenseQuery(elektraAppQueryOptions.project.distributionBoardDeviceListOptions({
        electricalProjectVersionId,
    }));

    const filteredDevices = useMemo(() => devices.data?.Devices?.filter(device => device.ElectricalProjectDistributionBoardId === distributionBoardId && deviceKinds.includes(device.DeviceKind as DeviceKind) && !device.IsDeleted) || [], [devices.data?.Devices, distributionBoardId, deviceKinds]);

    return {
        data: {
            devices: filteredDevices as Device[],
            count: filteredDevices.length,
        },
        isLoading: devices.isLoading,
        isError: devices.isError,
        error: devices.error,
    };
}

export function useDevice({
    electricalProjectVersionId,
    distributionBoardDeviceId,
}: {
    electricalProjectVersionId: string;
    distributionBoardDeviceId: string;
}) {
    if (!electricalProjectVersionId) {
        throw new Error('electricalProjectVersionId is required');
    }

    if (!distributionBoardDeviceId) {
        throw new Error('distributionBoardDeviceId is required');
    }

    const devices = useSuspenseQuery(elektraAppQueryOptions.project.distributionBoardDeviceListOptions({
        electricalProjectVersionId,
    }));

    const device = useMemo<Device | null>(() => devices.data?.Devices?.find(device => device.ElectricalProjectDistributionBoardDeviceId === distributionBoardDeviceId) || null, [devices.data?.Devices, distributionBoardDeviceId]);

    return {
        data: {
            device,
        },
        isLoading: devices.isLoading,
        isError: devices.isError,
        error: devices.error,
    };
}
